import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, EyeOff, Camera } from 'lucide-react';
import Avatar from '../Avatar';
import { updateAttendantMe, getApiErrorMessage, getAttendantMe } from '../../api/api';
import { resolveMediaUrl } from '../../utils/media';
import { showError, showSuccess } from '../../utils/toast';
import { getNotificationVolume, setNotificationVolume } from '../../utils/notifications';
import { prepareAttendantPhoto } from '../../utils/attendantPhoto.js';

const MIN_PASSWORD_LENGTH = 8;
const evaluatePasswordCriteria = (password) => {
  const value = password ?? '';
  return {
    summary: 'Use ao menos 8 caracteres, com maiúsculas, minúsculas e número.',
    checks: [
      { key: 'length', label: 'Pelo menos 8 caracteres.', pass: value.length >= MIN_PASSWORD_LENGTH },
      { key: 'upper', label: 'Inclui pelo menos uma letra maiúscula.', pass: /[A-Z]/.test(value) },
      { key: 'lower', label: 'Inclui pelo menos uma letra minúscula.', pass: /[a-z]/.test(value) },
      { key: 'number', label: 'Inclui pelo menos um número.', pass: /\d/.test(value) },
    ],
  };
};

function ModalEditProfile({
  open,
  onClose,
  user,
  onUpdated,
  container,
}) {
  const dialogRef = useRef(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    photograph: '',
    _photoChanged: false,
  });
  const [photoPreview, setPhotoPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [soundVolume, setSoundVolume] = useState(1); // 0 a 1
  const [showPasswordTips, setShowPasswordTips] = useState(false);
  const passwordStatus = useMemo(
    () => evaluatePasswordCriteria(formData.password),
    [formData.password]
  );

  useEffect(() => {
    if (!open) return undefined;

    const initialPhoto = user?.photograph || user?.avatarUrl || '';
    const initialVolume = (() => {
      const stored = getNotificationVolume();
      if (stored !== null && stored !== undefined) return stored;
      return user?.active_sound_notification === false ? 0 : 1;
    })();

    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      password: '',
      photograph: initialPhoto,
      _photoChanged: false,
    });
    setSoundVolume(initialVolume);
    setPhotoPreview(resolveMediaUrl(initialPhoto));
    setError('');
    setShowPassword(false);
    setShowPasswordTips(false);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => dialogRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, user]);

  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const prepared = await prepareAttendantPhoto(file);
      setPhotoPreview((previous) => {
        if (previous?.startsWith?.('blob:')) {
          URL.revokeObjectURL(previous);
        }
        return prepared.previewUrl;
      });
      setFormData((prev) => ({
        ...prev,
        photograph: prepared.dataUrl,
        _photoChanged: true,
      }));
    } catch (err) {
      const message = err?.message || 'Não foi possível processar a foto.';
      setError(message);
      showError(message);
    } finally {
      event.target.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const trimmedName = (formData.name || '').trim();
    const trimmedEmail = (formData.email || '').trim();

    if (!trimmedName) {
      setError('Informe o nome.');
      return;
    }
    if (!trimmedEmail) {
      setError('Informe o email.');
      return;
    }

    const payload = {
      name: trimmedName,
      email: trimmedEmail,
      ...(formData._photoChanged ? { photograph: formData.photograph || null } : {}),
    };

    if (formData.password) {
      payload.password = formData.password;
    }

    try {
      setIsSubmitting(true);
      const updated = await updateAttendantMe(payload);
      let latest = updated;
      try {
        latest = await getAttendantMe();
      } catch {
        latest = updated;
      }
      setNotificationVolume(soundVolume);
      onUpdated?.(latest);
      showSuccess('Perfil atualizado com sucesso!');
      onClose?.();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Erro ao atualizar perfil.');
      setError(message);
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = () => {
    if (!isSubmitting) onClose?.();
  };

  const handlePickPhoto = () => {
    if (isSubmitting) return;
    fileInputRef.current?.click();
  };

  const resolvedPreview = photoPreview || resolveMediaUrl(formData.photograph);

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-label="Editar perfil do usuário"
        onClick={(event) => event.stopPropagation()}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Editar perfil</h2>
          <button
            type="button"
            aria-label="Fechar"
            className="modal-icon-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X className="icon" />
          </button>
        </div>

        <p style={{ marginTop: -8, marginBottom: 0, color: 'rgba(226,232,240,0.8)' }}>
          Altere seus dados e preferências sem sair do chat.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Foto</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar
                name={formData.name || user?.name}
                photo={resolvedPreview}
                size={60}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                  disabled={isSubmitting}
                />
                <button type="button" onClick={handlePickPhoto} disabled={isSubmitting}>
                  <Camera size={16} style={{ marginRight: 6 }} />
                  Trocar foto
                </button>
                <small style={{ color: 'rgba(226,232,240,0.75)' }}>
                  PNG ou JPG. Se preferir, mantenha como está.
                </small>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Nome</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label>Senha</label>
            <div className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Deixe em branco para manter a senha atual"
                disabled={isSubmitting}
                onFocus={() => setShowPasswordTips(true)}
                onBlur={() => {
                  if (!formData.password) setShowPasswordTips(false);
                }}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <Eye className="password-toggle__icon" aria-hidden="true" />
                ) : (
                  <EyeOff className="password-toggle__icon" aria-hidden="true" />
                )}
              </button>
            </div>
            <small style={{ color: 'rgba(226,232,240,0.75)' }}>
              Use uma senha forte. Deixe vazio para não alterar.
            </small>
          </div>

          {(showPasswordTips || formData.password) && (
            <div
              className={`password-guidelines${passwordStatus.checks.every((c) => c.pass) ? ' password-guidelines--valid' : ''}`}
              aria-live="polite"
            >
              <div className="password-guidelines__header">
                <span className="password-guidelines__title">Dicas de senha</span>
                <span className="password-guidelines__state">
                  {passwordStatus.checks.every((c) => c.pass) ? 'Pronta' : 'Pendências'}
                </span>
              </div>
              <p className="password-guidelines__description">{passwordStatus.summary}</p>
              <ul className="password-guidelines__list">
                {passwordStatus.checks.map((item) => (
                  <li
                    key={item.key}
                    className={`password-guidelines__item${item.pass ? ' password-guidelines__item--pass' : ''}`}
                  >
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Volume das notificações sonoras</span>
            </label>
            <div style={{ maxWidth: 260 }}>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={Math.round(soundVolume * 100)}
                onChange={(event) => setSoundVolume(Number(event.target.value) / 100)}
                disabled={isSubmitting}
                style={{ width: '100%' }}
              />
            </div>
            <small style={{ color: 'rgba(226,232,240,0.75)' }}>
              Ajuste o volume do som de novos atendimentos e mensagens.
            </small>
          </div>

          {error && (
            <div className="modal-error-banner" role="alert">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting && <span className="btn-spinner" aria-hidden />}
              {isSubmitting ? 'Salvando...' : 'Salvar mudanças'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    container || document.body,
  );
}

export default ModalEditProfile;
